// src/components/Header.js
import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/AICE_logo.png';

const Header = () => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/ai-dag';
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isYdelserOpen, setIsYdelserOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const logoutTimerRef = useRef(null);
  const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1000;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
        setIsSolutionsOpen(false);
        setIsYdelserOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkAuth = () =>
      setIsLoggedIn(document.cookie.split(';').some((c) => c.trim().startsWith('aice_session=')));
    checkAuth();
    const onStorage = (e) => {
      if (e.key === 'token') checkAuth();
    };
    const onTokenChanged = () => checkAuth();
    const onFocus = () => checkAuth();
    window.addEventListener('storage', onStorage);
    window.addEventListener('token-changed', onTokenChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('token-changed', onTokenChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Reset menus whenever auth state or layout breakpoint changes to avoid stale open/closed UI
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSolutionsOpen(false);
    setIsYdelserOpen(false);
  }, [isLoggedIn, isMobile]);

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const handleLogout = async () => {
    clearLogoutTimer();
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (err) {
      // ignore network errors — cookies will expire naturally
    }
    localStorage.removeItem('username');
    localStorage.removeItem('companyId');
    setIsLoggedIn(false);
    window.dispatchEvent(new Event('token-changed'));
    window.location.replace('/login');
  };

  const resetInactivityTimer = () => {
    if (!document.cookie.split(';').some((c) => c.trim().startsWith('aice_session='))) return;
    clearLogoutTimer();
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_MS);
  };

  const scrollToFooter = () => {
    const footer = document.getElementById('site-footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const onActivity = () => resetInactivityTimer();
    events.forEach((evt) => window.addEventListener(evt, onActivity));
    resetInactivityTimer();
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, onActivity));
      clearLogoutTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  if (isLandingPage) {
    return (
      <header style={{ ...headerStyle, padding: '0.9rem 1.5rem' }}>
        <div style={headerInnerStyle}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={logoContainerStyle}>
              <img src={logo} alt="AICE Logo" style={logoStyle} />
            </div>
          </Link>
        </div>
      </header>
    );
  }

  return (
    <header
      style={{
        ...headerStyle,
        ...(isMobile
          ? {
              padding: '0.75rem 1rem',
            }
          : {}),
      }}
    >
      <div style={headerInnerStyle}>
        {/* Logo - clickable to return home */}
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={logoContainerStyle}>
            <img src={logo} alt="AICE Logo" style={{ ...logoStyle, cursor: 'pointer' }} />
          </div>
        </Link>

        {/* Navigation */}
        <nav style={{ ...navStyle, ...(isMobile ? { width: 'auto', justifyContent: 'flex-end' } : {}) }}>
        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          style={{
            display: isMobile ? 'inline-flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 10,
            border: '1px solid #f0f0f0',
            background: '#ffffff',
            cursor: 'pointer',
            marginLeft: 'auto',
          }}
        >
          <span style={{ fontSize: '1.4rem', color: '#C03626', lineHeight: 1 }}>☰</span>
        </button>

        <ul
          style={{
            ...navListStyle,
            ...(isMobile
              ? {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '0.8rem',
                  padding: isMenuOpen ? '1rem' : 0,
                  marginTop: isMenuOpen ? '0.75rem' : 0,
                  borderRadius: '12px',
                  boxShadow: isMenuOpen ? '0 10px 28px rgba(0,0,0,0.08)' : 'none',
                  background: '#fff',
                  display: isMenuOpen ? 'flex' : 'none',
                  position: 'absolute',
                  right: '1rem',
                  left: '1rem',
                  top: '72px',
                  zIndex: 1500,
                }
              : {}),
          }}
        >
          {isLoggedIn && <li><Link to="/agents" style={{ ...navLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => setIsMenuOpen(false)}>Agents</Link></li>}
          <li
            style={{ ...dropdownContainerStyle, ...(isMobile ? { width: '100%' } : {}) }}
            onMouseEnter={() => !isMobile && setIsYdelserOpen(true)}
            onMouseLeave={() => !isMobile && setIsYdelserOpen(false)}
          >
            {isMobile ? (
              <button
                type="button"
                style={{
                  ...dropdownToggleStyle,
                  fontSize: '0.95rem',
                  width: '100%',
                  justifyContent: 'space-between',
                  color: isYdelserOpen ? '#8C261C' : dropdownToggleStyle.color,
                }}
                onClick={() => setIsYdelserOpen((prev) => !prev)}
              >
                Ydelser
                <span aria-hidden="true" style={{ ...caretStyle, transform: isYdelserOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
              </button>
            ) : (
              <Link
                to="/ydelser"
                style={{ ...dropdownToggleStyle, textDecoration: 'none', color: isYdelserOpen ? '#8C261C' : dropdownToggleStyle.color }}
                onClick={() => setIsMenuOpen(false)}
              >
                Ydelser
                <span aria-hidden="true" style={{ ...caretStyle, transform: isYdelserOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
              </Link>
            )}
            {isYdelserOpen && (
              <ul style={{ ...dropdownMenuStyle, ...(isMobile ? { position: 'relative', boxShadow: 'none', marginTop: '0.4rem' } : {}) }}>
                <li style={dropdownItemStyle}>
                  <Link to="/ydelser/ai-kursus" style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => { setIsYdelserOpen(false); setIsMenuOpen(false); }}>
                    AI-kursus
                  </Link>
                </li>
                <li style={dropdownItemStyle}>
                  <Link to="/rasmus-kaltoft" style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => { setIsYdelserOpen(false); setIsMenuOpen(false); }}>
                    AI Coaching
                  </Link>
                </li>
                <li style={dropdownItemStyle}>
                  <Link to="/ydelser/ai-center-of-excellence" style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => { setIsYdelserOpen(false); setIsMenuOpen(false); }}>
                    AI Center of Excellence
                  </Link>
                </li>
                <li style={dropdownItemStyle}>
                  <Link to="/losninger" style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => { setIsYdelserOpen(false); setIsMenuOpen(false); }}>
                    Agenter
                  </Link>
                </li>
              </ul>
            )}
          </li>
          <li><Link to="/ai-insigter" style={{ ...navLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => setIsMenuOpen(false)}>AI-ledelse</Link></li>
          <li><Link to="/kundecases" style={{ ...navLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => setIsMenuOpen(false)}>Kundecases</Link></li>
          <li
            style={{ ...dropdownContainerStyle, ...(isMobile ? { width: '100%' } : {}) }}
            onMouseEnter={() => !isMobile && setIsSolutionsOpen(true)}
            onMouseLeave={() => !isMobile && setIsSolutionsOpen(false)}
          >
            {isMobile ? (
              <button
                type="button"
                style={{
                  ...dropdownToggleStyle,
                  fontSize: '0.95rem',
                  width: '100%',
                  justifyContent: 'space-between',
                  color: isSolutionsOpen ? '#8C261C' : dropdownToggleStyle.color,
                }}
                onClick={() => setIsSolutionsOpen((prev) => !prev)}
              >
                Agenter
                <span
                  aria-hidden="true"
                  style={{
                    ...caretStyle,
                    transform: isSolutionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▾
                </span>
              </button>
            ) : (
              <Link
                to="/losninger"
                style={{
                  ...dropdownToggleStyle,
                  textDecoration: 'none',
                  color: isSolutionsOpen ? '#8C261C' : dropdownToggleStyle.color,
                }}
                onClick={() => setIsMenuOpen(false)}
              >
                Agenter
                <span
                  aria-hidden="true"
                  style={{
                    ...caretStyle,
                    transform: isSolutionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  ▾
                </span>
              </Link>
            )}
            {isSolutionsOpen && (
              <ul style={{ ...dropdownMenuStyle, ...(isMobile ? { position: 'relative', boxShadow: 'none', marginTop: '0.4rem' } : {}) }}>
                <li style={dropdownItemStyle}>
                  <Link
                    to="/losninger/udbudsagent"
                    style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }}
                    onClick={() => { setIsSolutionsOpen(false); setIsMenuOpen(false); }}
                  >
                    Udbudsagent
                  </Link>
                </li>
                <li style={dropdownItemStyle}>
                  <Link
                    to="/losninger/emailagenten"
                    style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }}
                    onClick={() => { setIsSolutionsOpen(false); setIsMenuOpen(false); }}
                  >
                    Emailagenten
                  </Link>
                </li>
                {/*
                <li style={dropdownItemStyle}>
                  <Link
                    to="/losninger/plc-agent"
                    style={{ ...dropdownLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }}
                    onClick={() => { setIsSolutionsOpen(false); setIsMenuOpen(false); }}
                  >
                    PLC Agent
                  </Link>
                </li>
                */}
              </ul>
            )}
          </li>
          <li><Link to="/about" style={{ ...navLinkStyle, ...(isMobile ? { fontSize: '0.95rem' } : {}) }} onClick={() => setIsMenuOpen(false)}>Om</Link></li>

          {isMobile && (
            <li style={{ width: '100%', display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              {!isLoggedIn ? (
                <Link to="/login" style={{ textDecoration: 'none', flex: 1 }} onClick={() => setIsMenuOpen(false)}>
                  <button style={{ ...buttonStyleAlternate, width: '100%' }}>Log ind</button>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                  style={{ ...buttonStyleAlternate, width: '100%', background: '#FFF0EC' }}
                >
                  Log ud
                </button>
              )}
                  <Link
                to="/about#meet-team"
                style={{ flex: 1, textDecoration: 'none' }}
                onClick={() => setIsMenuOpen(false)}
              >
                <button style={{ ...buttonStyle, width: '100%' }}>Kontakt os</button>
              </Link>
            </li>
          )}
        </ul>
        </nav>

        {/* Buttons */}
        <div
          style={{
            ...buttonContainerStyle,
            display: isMobile ? 'none' : buttonContainerStyle.display,
          }}
        >
          {isLoggedIn && localStorage.getItem('companyId') === '698b28231646a2398c4a7824' && (
            <Link to="/aice" style={{ textDecoration: 'none' }}>
              <button
                type="button"
                style={buttonStyleAlternate}
                onMouseOver={(e) => { e.target.style.background = '#FFF0EC'; }}
                onMouseOut={(e) => { e.target.style.background = 'transparent'; }}
              >
                AICE
              </button>
            </Link>
          )}
          {!isLoggedIn ? (
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <button
                style={buttonStyleAlternate}
                onMouseOver={(e) => {
                  e.target.style.border = '1px solid #C03626';
                  e.target.style.background = '#FFF0EC';
                }}
                onMouseOut={(e) => {
                  e.target.style.border = '1px solid #C03626';
                  e.target.style.background = 'transparent';
                }}
              >
                Log ind
              </button>
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleLogout}
              style={{
                ...buttonStyleAlternate,
                background: '#FFF0EC',
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FFD9D0';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#FFF0EC';
              }}
            >
              Log ud
            </button>
          )}

          <Link to="/about#meet-team" style={{ textDecoration: 'none' }}>
            <button
              type="button"
              style={buttonStyle}
              onMouseOver={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#C03626';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #C03626, #FF6F61)';
                e.target.style.color = 'white';
              }}
            >
              Kontakt os
            </button>
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;

// ---------------------- Styles ----------------------

const headerStyle = {
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #f0f0f0',
  padding: '1rem 1.5rem',
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
};

const headerInnerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  position: 'relative',
};

const logoContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
};

const logoStyle = {
  height: '40px',
};

const navStyle = {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const navListStyle = {
  listStyle: 'none',
  display: 'flex',
  justifyContent: 'center',
  gap: '2rem',
  margin: 0,
  padding: 0,
};

const navLinkStyle = {
  textDecoration: 'none',
  color: '#C03626',
  fontSize: '1.05rem',
  fontWeight: '600',
  position: 'relative',
  transition: 'color 0.3s ease',
};

navLinkStyle[':hover'] = {
  color: '#8C261C',
};

// Optional underline animation
// You can add this dynamically in CSS/JS-in-CSS libs like styled-components,
// but here's a trick to do it inline with JS objects:
navLinkStyle.onMouseEnter = (e) => {
  e.target.style.color = '#8C261C';
  e.target.style.transform = 'scale(1.05)';
};
navLinkStyle.onMouseLeave = (e) => {
  e.target.style.color = '#C03626';
  e.target.style.transform = 'scale(1)';
};

const buttonContainerStyle = {
  display: 'flex',
  gap: '1rem',
};

const buttonStyle = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  fontSize: '1.02rem',
};

const buttonStyleAlternate = {
  background: 'transparent',
  color: '#C03626',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: '1px solid #C03626',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontSize: '1.02rem',
};

const dropdownContainerStyle = {
  position: 'relative',
};

const dropdownToggleStyle = {
  ...navLinkStyle,
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  border: 'none',
  background: 'transparent',
  padding: 0,
  cursor: 'pointer',
};

const caretStyle = {
  fontSize: '0.8rem',
  transition: 'transform 0.2s ease',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  backgroundColor: '#ffffff',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
  borderRadius: '8px',
  padding: '0.5rem 0',
  minWidth: '200px',
  zIndex: 1000,
};

const dropdownItemStyle = {
  listStyle: 'none',
};

const dropdownLinkStyle = {
  ...navLinkStyle,
  display: 'block',
  padding: '0.65rem 1rem',
  color: '#C03626',
  fontWeight: 600,
};
